import { useMemo } from 'react';
import { usePayrollPolicies } from '../../services/httpServices/queries';
import { SearchableSelect } from './searchable-select';

interface PayrollPolicySelectProps {
  value: string;
  onChange: (value: string) => void;
  /** Placeholder shown when no policy is selected (i.e. inheriting). */
  placeholder?: string;
  error?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Optional per-employee payroll policy picker.
 *
 * An empty value means "inherit": the employee uses their department policy,
 * or the global default when the department has none. Selecting a policy here
 * overrides that resolution for this employee only.
 */
export function PayrollPolicySelect({
  value,
  onChange,
  placeholder = 'Use department / default policy',
  error,
  disabled,
}: PayrollPolicySelectProps) {
  const { data, isLoading } = usePayrollPolicies();

  const options = useMemo(
    () =>
      (data?.data || [])
        .filter((policy) => policy.isActive)
        .map((policy) => ({
          value: policy.id,
          label: policy.name,
          description: policy.department?.name
            ? policy.department.name
            : policy.isDefault
              ? 'Default (all departments)'
              : 'Global policy',
        })),
    [data],
  );

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      loading={isLoading}
      error={error}
      disabled={disabled}
    />
  );
}
